/**
 * Intro content per competition format (specs/competitions-hub-client Slice 3D,
 * corrected in specs/champions-league Story 8).
 *
 * The World Cup keeps its own WORLD_CUP_WELCOME content — untouched.
 *
 * Content is **built from the competition**, not looked up as a frozen object. The point table
 * and the scoring sentence come from the server-resolved `rules`, which is the same config
 * settlement scores with, so the screen that explains the rules cannot state different ones.
 * The static objects below remain the fallback for a client that has no rules yet.
 */

import type {
  CompetitionIntroConfig,
  CompetitionIntroNote,
  CompetitionIntroRuleItem,
} from '../types/competitionIntro.types';
import type { CompetitionRulesSummary, CompetitionSummary } from '../types/competitions.types';
import { WORLD_CUP_COMPETITION_ID } from '../../worldcup/worldcup.constants';
import {
  buildIntroPointsRows,
  goldenBallNote,
  introFuturesDeadlineCopy,
  scoringRuleDescription,
  stageLabel,
} from '../helpers/competitionIntroPoints.helper';

/**
 * Bumped from v1 when the scoring copy was corrected. The storage key carries this, so everyone
 * who dismissed the wrong rules is shown the right ones once (see competitionIntroStorage.helper).
 */
const SEASON_CONTENT_ID = 'season-v2';
// v4: the futures deadline. A player who dismissed v3 was told both picks lock at the first
// kickoff; on a competition with `futures.lockAfterRound` that is now wrong in the direction
// that costs them picks, so they are shown the rules once more.
const TOURNAMENT_CONTENT_ID = 'tournament-v4';

/** Season pools: matchweeks, flat scoring, weekly + season boards, squads. */
export const SEASON_INTRO_CONTENT: CompetitionIntroConfig = {
  contentId: SEASON_CONTENT_ID,
  splash: {
    subtitle: 'Season pool',
    tagline: 'Call every matchweek. Climb the table. Beat your squad.',
    cta: "Let's Go",
  },
  rules: {
    label: 'How it works',
    title: 'Rules',
    subtitle: 'Four things to know before your first matchweek.',
    items: [
      {
        title: 'Predict every matchweek',
        icon: 'target',
        desc: 'Submit a score for each fixture before kickoff. Each match locks the moment it starts — the rest of the matchweek stays open.',
      },
      {
        title: 'Scoring',
        icon: 'scoring',
        desc: 'Exact score = 5 points. Right winner (or a draw) = 2 points. Every matchweek is worth the same, all season long.',
      },
      {
        title: 'Weekly and season tables',
        icon: 'table',
        desc: 'Your points feed two boards: this matchweek, and the whole season. A bad week never ends your run.',
      },
      {
        title: 'Squads',
        icon: 'squad',
        desc: 'Create or join a private squad and compete on your own leaderboard — the season is more fun against people you know.',
      },
    ],
    cta: 'See point system',
  },
  points: {
    label: 'Scoring',
    title: 'Point system',
    colRound: 'Result',
    colExact: 'Exact',
    colOutcome: 'Outcome',
    rows: [{ stage: 'Every matchweek', hit: 5, result: 2 }],
    notes: [
      {
        title: 'Exact score',
        body: 'Call 2-1 and the match ends 2-1 — that is 5 points. The full-time score is what counts.',
      },
      {
        title: 'Right outcome',
        body: 'Called the winner but not the score? 2 points. Predicting a draw and getting one counts too.',
      },
      {
        title: 'Missed a match?',
        body: 'No pick, no points — but nothing is deducted. Jump back in the next matchweek.',
      },
    ],
  },
  skip: 'Skip',
  ctaEnterApp: 'Start Predicting',
};

/**
 * Knockout tournaments other than the World Cup — the Champions League is the first.
 *
 * The previous version of this copy was written for a baseball postseason: it talked about the
 * "first pitch" and promised "no draws here — every game has a winner", which is wrong for a
 * league phase, and it published a flat 5/2 that no tournament actually pays.
 */
export const TOURNAMENT_INTRO_CONTENT: CompetitionIntroConfig = {
  contentId: TOURNAMENT_CONTENT_ID,
  splash: {
    subtitle: 'Tournament pool',
    tagline: 'Call every game. Survive every round. Take the title.',
    cta: "Let's Go",
  },
  rules: {
    label: 'How it works',
    title: 'Rules',
    subtitle: 'Five things to know before the first whistle.',
    items: [
      {
        title: 'Predict every game',
        icon: 'target',
        desc: 'Submit a score for each fixture. Every match locks 1′ before kickoff, and the rest of the round stays open.',
        titleSegments: [
          { text: 'Predict the score until ' },
          { text: '1′ before', em: true },
          { text: ' kickoff' },
        ],
        preview: 'countdown',
        note: 'Edit your score until 1′ before kickoff',
        noteSide: 'center',
      },
      {
        title: 'A fresh ×2 every round',
        icon: 'boost',
        desc: 'Double the points on one game per round. Place it before that game kicks off; once it starts, the ×2 is locked where you left it.',
        titleSegments: [
          { text: 'A fresh ×2 ' },
          { text: 'every round', em: true },
        ],
        preview: 'boost',
        note: 'One ×2 per round',
        noteSide: 'right',
      },
      {
        title: 'Regular time only',
        icon: 'clock',
        desc: 'The 90′ score settles your pick. Extra time and penalties decide who goes through, but they never change your points.',
        titleSegments: [
          { text: 'Regular time only, the ' },
          { text: '90′', em: true },
          { text: ' score' },
        ],
        preview: 'live',
        note: 'Only the 90′ score counts',
        noteSide: 'right',
      },
      {
        title: 'The rounds get bigger',
        icon: 'scoring',
        desc: 'Every knockout round is worth more than the one before it, and the final is worth the most of all. The next screen has the exact numbers.',
      },
      {
        title: 'Squads',
        icon: 'squad',
        desc: 'Create or join a private squad and race your friends through the rounds.',
      },
    ],
    cta: 'See point system',
  },
  points: {
    label: 'Scoring',
    title: 'Point system',
    colRound: 'Round',
    colExact: 'Exact',
    colOutcome: 'Outcome',
    goldenBallLabel: 'Double Points',
    // The default ladder, which is what a tournament that overrides nothing actually pays
    // (`STAGE_POINTS` + `REGULAR_SEASON_STAGE_POINTS` on the server). This is only ever shown
    // when the competition sent no `stagePoints`; a flat "every game 5 / 2" sat here before and
    // was the defect Story 8 exists to fix, so the fallback must not restate it.
    rows: [
      { stage: 'League phase', hit: 5, result: 2 },
      { stage: 'Round of 32', hit: 5, result: 3 },
      { stage: 'Round of 16', hit: 7, result: 4 },
      { stage: 'Quarter-Finals', hit: 10, result: 6 },
      { stage: 'Semi-Finals', hit: 14, result: 8 },
      { stage: 'Final', hit: 20, result: 12 },
    ],
    notes: [
      {
        title: 'Exact score',
        body: 'Call 2-1 and the match ends 2-1. That is the exact-score column, in full.',
      },
      {
        title: 'Right outcome',
        body: 'Wrong score but the right winner, or a draw you called, takes the outcome column.',
      },
      {
        title: 'The rounds get bigger',
        body: 'A knockout game is worth more than a league-phase one, and the final is worth the most of all.',
      },
    ],
  },
  skip: 'Skip',
  ctaEnterApp: 'Start Predicting',
};

/**
 * Overlay this competition's real numbers onto the format's copy.
 *
 * Every derived field falls back to the static content, so a competition whose server has not
 * shipped `stagePoints` yet reads exactly as it did before rather than showing an empty table.
 */
/**
 * The winner / top-scorer rule and its point note, in this competition's real bonus.
 *
 * Added rather than authored into the static content: a competition that does not offer
 * futures must say nothing about them, and the static copy is exactly what a client running
 * ahead of its server renders (Story 15, AC-15.8).
 */
function futuresCopy(
  bonusPts: number,
  lockAfterRound: { stage: string; round: string } | null | undefined,
): {
  rule: CompetitionIntroRuleItem;
  note: CompetitionIntroNote;
} {
  // When the competition holds the window open past its first kickoff (AC-15.11), the rules
  // have to say so — "locks at the very first kickoff" would send players hunting for a
  // deadline that has already passed, and would undersell the time they actually have.
  const deadline = introFuturesDeadlineCopy(lockAfterRound);
  return {
    rule: {
      title: 'Winner and top scorer',
      icon: 'futures',
      desc: `Call who lifts the trophy and who ends up top scorer. ${deadline.rule} Once they `
        + `lock they stay sealed all the way to the final, so pick once and live with it. `
        + `Each one right is worth ${bonusPts} points.`,
    },
    note: {
      title: 'Winner and top scorer',
      body: `Two long shots, ${bonusPts} points each, ${deadline.note}. They are `
        + 'settled once the competition is over.',
    },
  };
}

function withCompetitionRules(
  base: CompetitionIntroConfig,
  rules: CompetitionRulesSummary | null | undefined,
): CompetitionIntroConfig {
  const rows = buildIntroPointsRows(rules);
  if (rows.length === 0) return base;

  const futures = rules?.futures?.enabled === true && (rules.futures.bonusPts ?? 0) > 0
    ? futuresCopy(rules.futures.bonusPts, rules.futures.lockAfterRound)
    : null;

  const items: CompetitionIntroRuleItem[] = base.rules.items.map((item) => {
    if (item.icon === 'scoring' && item.preview == null) {
      return { ...item, desc: scoringRuleDescription(rows, item.desc) };
    }
    if (item.preview === 'boost') {
      return { ...item, note: goldenBallNote(rules, item.note ?? '') };
    }
    return item;
  });

  return {
    ...base,
    rules: {
      ...base.rules,
      // Last, after the mechanics: a future is the one pick a player makes before anything
      // else has happened, so it reads better once the round-by-round rules are established.
      items: futures ? [...items, futures.rule] : items,
    },
    points: {
      ...base.points,
      notes: futures ? [...base.points.notes, futures.note] : base.points.notes,
      rows,
      // Labels, not stage keys: the timeline marks a card by the name the row carries, so it
      // never has to know this competition's stage vocabulary.
      goldenBallStages: (rules?.goldenBallStages ?? []).map((stage) => stageLabel(stage, rules)),
    },
  };
}

/**
 * Intro content for a competition, or null when it has its own flow (World Cup).
 */
export function getCompetitionIntroContent(
  competition: Pick<CompetitionSummary, 'id' | 'format' | 'rules'> | null,
): CompetitionIntroConfig | null {
  if (competition == null) return null;
  // The World Cup owns WORLD_CUP_WELCOME (host flags, stage ladder, futures previews).
  if (competition.id === WORLD_CUP_COMPETITION_ID) return null;

  const base = competition.format === 'SEASON' ? SEASON_INTRO_CONTENT : TOURNAMENT_INTRO_CONTENT;
  return withCompetitionRules(base, competition.rules);
}
